import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { useMemo } from "react";

import { useCreateEcrDocMutation } from "api/memberPortal/ecr/docs/ecrDocs.api";
import { useEcrCreateMutation } from "api/memberPortal/ecr/ecr.api";
import { EcrDocPayload, EcrPayload } from "api/memberPortal/ecr/ecr.types";
import {
  ECR_FILE_TYPES,
  ECR_PROCESS_LOCATIONS,
  ECR_STATES,
  ECR_STATUSES,
  ECR_TYPES,
} from "api/memberPortal/ecr/ecrOptions.types";
import { useCreateEcrTdMutation } from "api/memberPortal/ecr/tds/ecrTds.api";
import { useUploadFileMutation } from "api/memberPortal/files/files.api";
import { FILE_VISIBILITY_TYPES } from "api/memberPortal/files/files.types";
import { useLazyGetAllProjectPpByProductIdQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import { useLazyGetAllTdsByIdQuery } from "api/memberPortal/technicalDecisions/technicalDecisions.api";
import { useAppForm } from "components/Forms/AppForm";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

import EcrFileCard from "./components/EcrFIleCard";
import EcrProductSelect from "./components/EcrProductSelect";
import TdDisplayList from "./components/TdDisplayList";
import { EcrFormInput, EcrFormSchema } from "./Ecr.schema";

export interface EcrFile {
  fileType: string;
  fileLabel: string;
  documentVisibility: string;
  file: File;
}

const options = {
  processLocations: Object.values(ECR_PROCESS_LOCATIONS),
  types: Object.values(ECR_TYPES),
  statuses: Object.values(ECR_STATUSES),
  documentTypes: Object.values(ECR_FILE_TYPES),
  documentVisibilities: Object.values(FILE_VISIBILITY_TYPES),
  uploadTypes: ["EAR Review", "Security Target", "Product Information"],
};

const defaultValues = {
  product: undefined,
  title: "",
  process_location: ECR_PROCESS_LOCATIONS.CHECK_OUT,
  type: "",
  status: "",
  documentTypes: [],
  cite: "",
  fileType: "",
  documentVisibility: "",
  fileLabel: "",
  files: [],
  comment: "",
  rationale: "",
  protectionProfile: undefined,
  technicalDecisions: [],
  requirement: "",
} as EcrFormInput;

interface EcrCommentFormProps {
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (_error: Error) => void;
}

const EcrCommentForm = ({
  show,
  onClose,
  onSuccess,
  onError,
}: EcrCommentFormProps) => {
  const { currentUser } = useAuth();
  const [triggerTds, { data: tds }] = useLazyGetAllTdsByIdQuery();
  const [triggerEcr] = useEcrCreateMutation();
  const [triggerFileUpload] = useUploadFileMutation();
  const [triggerEcrTd] = useCreateEcrTdMutation();
  const [triggerDocType] = useCreateEcrDocMutation();
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const ecrForm = useAppForm({
    defaultValues,
    validators: {
      onSubmit: EcrFormSchema,
    },

    onSubmit: async ({ value }) => {
      const { data: parsed, success, error } = EcrFormSchema.safeParse(value);

      if (!parsed || !success) {
        // TODO: something with the errors?
        console.log("error", error);
        return;
      }

      const payload = {
        active: true,
        cite: parsed.cite,
        cmnt: parsed.comment,
        creator: currentUser.id,
        entrydate: getISOCurrentDate(), // TODO: move to backend
        gist: parsed.title,
        pid: parsed.product.value,
        pp_id: parsed.protectionProfile?.value,
        process_location: parsed.process_location,
        rationale: parsed.rationale,
        req: parsed.requirement,
        state: ECR_STATES.PROPOSED,
        status: parsed.status,
        type: parsed.type,
      } as EcrPayload;

      showLoadingSpinner("Creating ECR Comment");
      triggerEcr(payload)
        .unwrap()
        .then((data) => {
          const { files, technicalDecisions, documentTypes } = parsed;
          const { ecrid } = data;

          if (files.length > 0) {
            files.forEach(async (file) => {
              const fileData = new FormData();
              fileData.set("file", file.file);
              fileData.set("file_type_id", ecrid.toString());
              fileData.set("file_type", "ecr-comment");
              fileData.set("active", "true");
              fileData.set("file_name", file.file.name);
              fileData.set("file_label", file.fileLabel);
              fileData.set(
                "private",
                file.documentVisibility === "Public" ? "false" : "true",
              );
              fileData.set("uploaded_on", getISOCurrentDate()); // TODO: Move to backend

              triggerFileUpload(fileData);
              // TODO: something here
              // .then((data) => console.log("data", data))
              // .catch((e) => console.log("e", e));
            });
          }

          // TODO: The only thing that changes here is the td_id
          // It would be a lot better to move this to the backend
          // and submit the array of td_ids as part of the ECR payload
          // instead of firing multiple requests
          if (technicalDecisions.length > 0) {
            technicalDecisions.forEach((td) => {
              const payload = {
                ecrid,
                td_id: td.value,
                creator: currentUser.id,
                entrydate: getISOCurrentDate(),
              };
              triggerEcrTd(payload);
            });
          }

          // TODO: The only thing that changes here is the doc_type
          // It would be a lot better to move this to the backend
          // and submit the array of doc_types as part of the ECR payload
          // instead of firing multiple requests
          if (documentTypes) {
            documentTypes.forEach((docType) => {
              const payload = {
                ecrid,
                doc_type: docType,
                creator: currentUser.id,
                entrydate: getISOCurrentDate(),
              } as EcrDocPayload;
              triggerDocType(payload);
            });
          }
          onSuccess && onSuccess();
        })
        // TODO: something with errors
        .catch((e) => onError && onError(e))
        .finally(() => {
          hideLoadingSpinner();
          onClose();
        });
    },
  });

  const [triggerPps, { data: protectionProfiles }] =
    useLazyGetAllProjectPpByProductIdQuery();

  const ppOptions = useMemo(() => {
    if (!protectionProfiles) return [];

    return protectionProfiles.map((pp) => ({
      value: pp.pp__pp_id || "",
      label: pp.pp__pp_short_name || "",
    }));
  }, [protectionProfiles]);

  const tdOptions = useMemo(() => {
    if (!tds) return [];

    return tds.map((td) => ({
      label: td.identifier ?? td.title ?? "",
      value: td.td_id,
    }));
  }, [tds]);

  return (
    <Dialog
      open={show}
      fullScreen
      sx={{
        padding: "1em",
        margin: 0,
      }}
      slotProps={{
        paper: {
          sx: {
            margin: "64px",
            "&.MuiDialog-paperScrollBody": {
              maxWidth: "900px",
            },
          },
        },
      }}
      maxWidth={"md"}
      onClose={onClose}
      scroll="body"
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <ecrForm.AppForm>
          <DialogTitle>
            {/* Product */}
            <ecrForm.AppField
              name="product"
              listeners={{
                onChange: ({ value }) => {
                  ecrForm.resetField("protectionProfile");
                  ecrForm.resetField("technicalDecisions");
                  if (!value) return;
                  triggerPps(value.value);
                },
              }}
            >
              {() => (
                <>
                  <Typography
                    variant={"body1"}
                    fontWeight="bold"
                    marginBottom={1}
                  >
                    Add ECR Comment For
                  </Typography>
                  <EcrProductSelect />
                </>
              )}
            </ecrForm.AppField>

            {/* TODO: MOVE INTO FORM COMPONENTS */}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider
            variant="fullWidth"
            sx={{ opacity: ".25", borderColor: "black" }}
          />

          <DialogContent>
            {/* Comment Title */}
            <ecrForm.AppField name="title">
              {(field) => <field.TextInput label="Comment Title" />}
            </ecrForm.AppField>

            <ecrForm.FormSection title="Comment Info" columns={2}>
              {/* Process Location */}
              <ecrForm.AppField name="process_location">
                {(field) => (
                  <field.SelectField
                    options={options.processLocations}
                    label="Process Location"
                    required={true}
                    containerProps={{
                      sx: {
                        flex: "1 1 100%",
                      },
                    }}
                  />
                )}
              </ecrForm.AppField>

              {/* Comment Type */}
              <ecrForm.AppField name="type">
                {(field) => (
                  <field.SelectField
                    options={options.types}
                    label="Comment Type"
                    required={true}
                  />
                )}
              </ecrForm.AppField>

              {/* Comment Status */}
              <ecrForm.AppField name="status">
                {(field) => (
                  <field.SelectField
                    options={options.statuses}
                    label="Comment Status"
                    required={true}
                  />
                )}
              </ecrForm.AppField>

              {/* Document Types */}
              <ecrForm.AppField name="documentTypes">
                {(field) => (
                  <field.MultiSelect
                    options={options.documentTypes}
                    label="Document Types"
                    required={true}
                  />
                )}
              </ecrForm.AppField>

              {/* Cite */}
              <ecrForm.AppField name="cite">
                {(field) => (
                  <field.TextInput
                    required={true}
                    label="Cite (Page & Paragraph)"
                  />
                )}
              </ecrForm.AppField>
            </ecrForm.FormSection>

            <ecrForm.FormSection
              title="Attach Files"
              columns={4}
              fieldContainerProps={{ sx: { alignItems: "baseline" } }}
            >
              {/* File Type */}
              <ecrForm.AppField
                name="fileType"
                listeners={{
                  onChange: ({ value }) => {
                    ecrForm.setFieldValue("fileLabel", value ?? "");
                  },
                }}
              >
                {(field) => (
                  <field.SelectField
                    options={options.uploadTypes}
                    label="Type"
                    sx={{ flex: 1 }}
                  />
                )}
              </ecrForm.AppField>

              {/* Document Visibility */}
              <ecrForm.AppField name="documentVisibility">
                {(field) => (
                  <field.SelectField
                    options={options.documentVisibilities}
                    label="Document Visibility"
                    sx={{ flex: 1 }}
                  />
                )}
              </ecrForm.AppField>

              {/* File Label */}
              <ecrForm.AppField name="fileLabel">
                {(field) => (
                  <field.TextInput
                    label="File Label"
                    sx={{
                      "&": {
                        flex: "2 2 50%",
                        maxWidth: "calc(50% - 8px)",
                        height: "100%",
                        alignSelf: "baseline",
                      },
                    }}
                  />
                )}
              </ecrForm.AppField>

              {/* File Upload Button */}
              <ecrForm.Subscribe
                selector={(state) => {
                  const { fileType, fileLabel, documentVisibility } =
                    state.values;

                  //  Sets up the `disabled` prop down below
                  return !fileType || !fileLabel || !documentVisibility;
                }}
              >
                {/* `disabled` comes from the return value of the Subscribe above */}
                {(disabled) => (
                  <ecrForm.AppField
                    name="files"
                    listeners={{
                      onChange: () => {
                        ecrForm.resetField("fileType");
                        ecrForm.resetField("documentVisibility");
                        ecrForm.resetField("fileLabel");
                      },
                    }}
                  >
                    {(field) => (
                      <field.FileUpload
                        disabled={disabled}
                        label="Choose File"
                        annotateFile={(_file) => {
                          const { getFieldValue } = ecrForm;

                          const fileType = getFieldValue("fileType");
                          const documentVisibility =
                            getFieldValue("documentVisibility");
                          const fileLabel = getFieldValue("fileLabel");

                          return {
                            fileType,
                            documentVisibility,
                            fileLabel,
                          };
                        }}
                      />
                    )}
                  </ecrForm.AppField>
                )}
              </ecrForm.Subscribe>

              {/* File Display List */}
              <Box
                flex="1 1 100%"
                display="flex"
                gap={2}
                flexWrap="wrap"
                sx={{
                  marginLeft: 1,
                  paddingLeft: 1,
                }}
              >
                <ecrForm.AppField name="files" mode="array">
                  {(field) =>
                    field.state.value.map((file, idx) => (
                      <EcrFileCard
                        file={file}
                        index={idx}
                        key={`${file.file.name}`}
                      />
                    ))
                  }
                </ecrForm.AppField>
              </Box>
            </ecrForm.FormSection>

            {/* Comment */}
            <Box marginY={3}>
              <ecrForm.AppField name="comment">
                {(field) => <field.HtmlEditor label="COMMENT *" />}
              </ecrForm.AppField>
            </Box>

            {/* Rationale */}
            <Box marginY={3}>
              <ecrForm.AppField name="rationale">
                {(field) => <field.HtmlEditor label="RATIONALE" />}
              </ecrForm.AppField>
            </Box>

            <ecrForm.FormSection title="Protection Profiles" columns={2}>
              {/* Protection Profiles */}
              <ecrForm.AppField
                name="protectionProfile"
                listeners={{
                  onChange: ({ value }) => {
                    ecrForm.resetField("technicalDecisions");

                    if (!value) return;
                    triggerTds(value.value);
                  },
                }}
              >
                {(field) => (
                  <field.AutocompleteSelect
                    label="Protection Profile"
                    options={ppOptions}
                  />
                )}
              </ecrForm.AppField>

              <ecrForm.AppField name="requirement">
                {/* Requirement */}
                {(field) => (
                  <field.TextInput label="Requirement" fullWidth={false} />
                )}
              </ecrForm.AppField>
            </ecrForm.FormSection>

            <ecrForm.FormSection title="Technical Decisions" columns={2}>
              {/* Technical Decisions */}
              <ecrForm.AppField name="technicalDecisions">
                {(field) => (
                  <>
                    <field.MultiAutocompleteSelect
                      label="Technical Decisions"
                      options={tdOptions}
                      sx={{ marginBottom: 1 }}
                    />
                  </>
                )}
              </ecrForm.AppField>
              <ecrForm.AppField name="technicalDecisions">
                {(_field) => <TdDisplayList />}
              </ecrForm.AppField>
            </ecrForm.FormSection>
          </DialogContent>

          <Paper elevation={1} sx={{ zIndex: 100 }}>
            <DialogActions sx={{ padding: 1.5 }}>
              <ecrForm.Subscribe selector={(state) => state.canSubmit}>
                {(canSubmit) =>
                  !canSubmit && (
                    <Alert sx={{ flex: 1 }} severity="error">
                      Please correct the errors above before submitting
                    </Alert>
                  )
                }
              </ecrForm.Subscribe>
              <Button
                variant="contained"
                color="error"
                sx={{ width: 150 }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  minWidth: 150,
                }}
                type="submit"
                onClick={ecrForm.handleSubmit}
              >
                Add Comment
              </Button>
            </DialogActions>
          </Paper>
        </ecrForm.AppForm>
      </form>
    </Dialog>
  );
};
export default EcrCommentForm;
